/**
 * Compress an image file using canvas.
 * Resizes to max 1200px width, JPEG quality 0.65, targets <500KB.
 * No external dependencies.
 */
export function compressImage(file, { maxWidth = 1200, quality = 0.65, maxSizeKB = 500 } = {}) {
  return new Promise((resolve) => {
    // Skip non-image files
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Only downscale, never upscale
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // First pass at target quality
      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return; }

          // If still over target, reduce quality further
          if (blob.size > maxSizeKB * 1024 && quality > 0.3) {
            canvas.toBlob(
              (blob2) => {
                const compressed = new File(
                  [blob2 || blob],
                  file.name.replace(/\.[^.]+$/, '.jpg'),
                  { type: 'image/jpeg' }
                );
                resolve(compressed);
              },
              'image/jpeg',
              quality - 0.2
            );
          } else {
            const compressed = new File(
              [blob],
              file.name.replace(/\.[^.]+$/, '.jpg'),
              { type: 'image/jpeg' }
            );
            resolve(compressed);
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // Fallback to original
    };

    img.src = url;
  });
}
